import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { ClipboardCheck, Save, History, TrendingDown, TrendingUp } from 'lucide-react';

const OSWESTRY_CHOICES = [
  ['I have no pain at the moment.','The pain is very mild at the moment.','The pain is moderate at the moment.','The pain is fairly severe at the moment.','The pain is very severe at the moment.','The pain is the worst imaginable at the moment.'],
  ['I can look after myself normally without causing extra pain.','I can look after myself normally but it causes extra pain.','It is painful to look after myself and I am slow and careful.','I need some help but manage most of my personal care.','I need help every day in most aspects of self-care.','I do not get dressed, I wash with difficulty and stay in bed.'],
  ['I can lift heavy weights without extra pain.','I can lift heavy weights but it gives extra pain.','Pain prevents me from lifting heavy weights off the floor but I can if they are conveniently placed.','Pain prevents me from lifting heavy weights but I can manage light to medium weights.','I can lift only very light weights.','I cannot lift or carry anything.'],
  ['Pain does not prevent me from walking any distance.','Pain prevents me walking more than 2 km.','Pain prevents me walking more than 1 km.','Pain prevents me walking more than 100m.','I can only walk using a stick or crutches.','I am in bed most of the time.'],
  ['I can sit in any chair as long as I like.','I can sit in my favorite chair as long as I like.','Pain prevents me from sitting more than 1 hour.','Pain prevents me from sitting more than 30 minutes.','Pain prevents me from sitting more than 10 minutes.','Pain prevents me from sitting at all.'],
  ['I can stand as long as I want without extra pain.','I can stand as long as I want but it gives me extra pain.','Pain prevents me from standing for more than 1 hour.','Pain prevents me from standing for more than 30 minutes.','Pain prevents me from standing for more than 10 minutes.','Pain prevents me from standing at all.'],
  ['My sleep is never disturbed by pain.','My sleep is occasionally disturbed by pain.','Because of pain I have less than 6 hours sleep.','Because of pain I have less than 4 hours sleep.','Because of pain I have less than 2 hours sleep.','Pain prevents me from sleeping at all.'],
  ['My sex life is normal and causes no extra pain.','My sex life is normal but causes some extra pain.','My sex life is nearly normal but is very painful.','My sex life is severely restricted by pain.','My sex life is nearly absent because of pain.','Pain prevents any sex life at all.'],
  ['My social life is normal and gives me no extra pain.','My social life is normal but increases the degree of pain.','Pain has no significant effect on my social life apart from limiting energetic interests.','Pain has restricted my social life and I do not go out as often.','Pain has restricted my social life to my home.','I have no social life because of pain.'],
  ['I can travel anywhere without pain.','I can travel anywhere but it gives me extra pain.','Pain is bad but I manage journeys over 2 hours.','Pain restricts me to journeys less than 1 hour.','Pain restricts me to short necessary journeys under 30 minutes.','Pain prevents me from travelling except to receive treatment.'],
];

const QUICKDASH_LEVELS = ['No difficulty', 'Mild', 'Moderate', 'Severe', 'Unable'];

export default function PatientOutcomes() {
  const { patient_id: routeId } = useParams();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'clinician';
  const targetId = isAdmin ? routeId : user?.user_id;
  const navigate = useNavigate();

  const [tab, setTab] = useState('oswestry');
  const [history, setHistory] = useState([]);
  const [meta, setMeta] = useState(null);
  const [answers, setAnswers] = useState({});

  useEffect(() => {
    if (!targetId) return;
    api.get('/outcomes/meta/instruments').then(r => setMeta(r.data));
    api.get(`/outcomes/${targetId}`).then(r => setHistory(r.data || [])).catch(() => {});
    setAnswers({});
  }, [targetId, tab]);

  const sections = tab === 'oswestry' ? meta?.oswestry?.sections : meta?.quickdash?.questions;
  const len = sections?.length || 0;

  const submit = async () => {
    const ans = Array.from({ length: len }, (_, i) =>
      answers[i] === undefined ? null : answers[i]);
    try {
      const { data } = await api.post('/outcomes', { patient_id: targetId, instrument: tab, answers: ans });
      const refresh = await api.get(`/outcomes/${targetId}`);
      setHistory(refresh.data || []);
      setAnswers({});
      alert(`Saved.\n${tab.toUpperCase()} score: ${data.score ?? data.percent ?? '—'}${data.percent !== undefined ? '%' : ''}\n${data.interpretation || ''}`);
    } catch (e) {
      alert('Save failed: ' + (e.response?.data?.detail || e.message));
    }
  };

  if (!targetId) return <div className="p-8 text-fg-muted">Loading…</div>;

  return (
    <div className="p-8 max-w-4xl" data-testid="outcomes-page">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-[0.2em] text-brand font-semibold mb-2">FUNCTIONAL OUTCOMES</div>
        <h1 className="text-4xl font-heading font-bold tracking-tight">Outcome measures</h1>
        <p className="text-fg-muted mt-2 max-w-2xl text-sm">
          Standardised questionnaires that quantify your functional disability.
          Re-taking them weekly lets your therapist objectively measure progress.
        </p>
      </div>

      <div className="flex gap-2 mb-6">
        <button onClick={()=>setTab('oswestry')}
                className={`px-4 py-2 rounded-md text-sm font-medium ${tab==='oswestry' ? 'bg-brand text-white' : 'surface border border-soft text-fg-muted hover:text-fg'}`}
                data-testid="tab-oswestry">
          Oswestry (low-back disability)
        </button>
        <button onClick={()=>setTab('quickdash')}
                className={`px-4 py-2 rounded-md text-sm font-medium ${tab==='quickdash' ? 'bg-brand text-white' : 'surface border border-soft text-fg-muted hover:text-fg'}`}
                data-testid="tab-quickdash">
          QuickDASH (upper limb)
        </button>
      </div>

      <div className="bento-card mb-6">
        <h3 className="font-heading font-semibold mb-3 flex items-center gap-2">
          <ClipboardCheck className="w-4 h-4 text-brand"/>
          {tab === 'oswestry' ? 'Oswestry — Low back disability' : 'QuickDASH — Upper limb disability'}
        </h3>
        {!sections ? <div className="text-fg-muted text-sm">Loading…</div> : (
          <div className="space-y-5">
            {sections.map((label, i) => (
              <div key={i} data-testid={`outcome-q-${i}`}>
                <div className="text-sm font-medium mb-1.5">{i+1}. {label}</div>
                <div className="grid gap-1.5">
                  {tab === 'oswestry'
                    ? OSWESTRY_CHOICES[i].map((c, j) => (
                        <label key={j} className="flex items-start gap-2 text-sm cursor-pointer hover:bg-[var(--bg-tertiary)] rounded px-2 py-1.5">
                          <input type="radio" name={`q-${i}`} checked={answers[i] === j}
                                 onChange={()=>setAnswers(a => ({...a, [i]: j}))}
                                 className="mt-1 accent-[var(--brand)]"
                                 data-testid={`q-${i}-opt-${j}`}/>
                          <span>{c}</span>
                        </label>
                      ))
                    : [1,2,3,4,5].map((v, j) => (
                        <label key={v} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-[var(--bg-tertiary)] rounded px-2 py-1.5">
                          <input type="radio" name={`q-${i}`} checked={answers[i] === v}
                                 onChange={()=>setAnswers(a => ({...a, [i]: v}))}
                                 className="accent-[var(--brand)]"
                                 data-testid={`q-${i}-opt-${j}`}/>
                          <span><strong>{v}</strong> — {QUICKDASH_LEVELS[j]}</span>
                        </label>
                      ))}
                </div>
              </div>
            ))}
          </div>
        )}
        <div className="mt-6 flex justify-end">
          <button onClick={submit} className="btn-primary inline-flex items-center gap-2"
                  disabled={Object.keys(answers).length < Math.min(5, len)}
                  data-testid="outcome-submit">
            <Save className="w-4 h-4"/> Save & score
          </button>
        </div>
      </div>

      {history.length > 0 && (
        <div className="bento-card">
          <h3 className="font-heading font-semibold mb-3 flex items-center gap-2">
            <History className="w-4 h-4 text-brand"/> History
          </h3>
          <div className="space-y-2" data-testid="outcomes-history">
            {history.filter(h => h.instrument === tab).map((h, i, arr) => {
              const prev = arr[i + 1];
              const delta = (h.percent ?? h.score) - (prev ? (prev.percent ?? prev.score) : null);
              const improved = delta != null && delta < 0;  // lower score = less disability
              const worsened = delta != null && delta > 0;
              return (
                <div key={h.outcome_id} className="flex items-center justify-between surface border border-soft rounded-lg p-3 text-sm">
                  <div>
                    <div className="font-medium">{h.percent != null ? `${h.percent}%` : h.score != null ? `${h.score}` : '—'}</div>
                    <div className="text-xs text-fg-muted">{h.interpretation}</div>
                  </div>
                  <div className="flex items-center gap-3">
                    {improved && <span className="badge-success inline-flex items-center gap-1"><TrendingDown className="w-3 h-3"/> {Math.abs(delta).toFixed(1)}</span>}
                    {worsened && <span className="badge-warn inline-flex items-center gap-1"><TrendingUp className="w-3 h-3"/> +{delta.toFixed(1)}</span>}
                    <span className="text-xs text-fg-muted">{(h.submitted_at || '').slice(0, 10)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
